/**
 * Seminar feature store (Zustand) — the wired-in source of truth for the seminar
 * workspace (imported across the seminar components/pages/hooks).
 *
 * The list itself lives in TanStack Query (['seminars', activeSeminarQuery ??
 * null]); this store owns selection, the active filter, the edit draft and the
 * UI/modal flags, and drives the query layer (fetchQuery / invalidateQueries).
 * It consolidates the behaviour once spread across AppStateContext, useAppData
 * and the useSeminar* hooks into one store with options-object actions.
 */
import { create } from "zustand";

import { queryClient } from "../lib/queryClient";
import { seminarApi, referentApi, seminarParticipantApi } from "../services/endpoints";
import { apiId, relationId } from "../utils/identity";
import {
  toSeminarPayload,
  toSeminarCreatePayload,
  toSeminarWpLinkagePayload,
} from "../services/serializers/seminar";
import type { SeminarFormInput, WpPostResult } from "../services/serializers/seminar";
import { seminarsQueryKey, seminarsQueryOptions } from "../hooks/queries/useSeminars";
import { clientsQueryKey } from "../hooks/queries/useClients";
import type { SeminarQueryParams } from "../hooks/queries/useSeminars";
import { roomsQueryKey } from "../hooks/queries/useRooms";
import { sanitizeSeminarValue } from "../utils/seminarFields";
import { syncSeminarToWp, syncReferentsToWp } from "../services/wordpress/seminarWp";
import { notify } from "../services/notify";
import { MSG } from "../services/messages";
import { getErrorMessage, getFieldErrors, unwrap } from "../services/response";
import type { StrapiFieldError } from "../services/response";
import { addDays, toISODateString } from "../utils/dateUtils";
import { applyUpdater, makeSetters } from "./storeUtils";
import type { Updater, FieldValue } from "./storeUtils";
import type { Identifiable, ParticipantRow, Raeume, Seminar } from "../types/entities";
import type { OpenMode } from "../types/ui";
import type { ApiResponse } from "../types/api";
// Type-only (erased at runtime, so no import cycle with the hook that consumes
// this store): the room-picker query shape, owned by useSeminarRoomSelection and
// reused here for the migrated `roomQuery` state.
import type { RoomQuery } from "../hooks/seminars/useSeminarRoomSelection";

/** Which dynamic filter search is in flight (drives the per-button spinner), or none. */
type LoadingDyn = string | false;

/** The raw filter form values (mirrors useSeminarFilters' `query`). */
interface FilterQuery {
  semid: string;
  thema: string;
  // Client-side name filter over the seminars' referents (see
  // domain/seminarRules.filterSeminarsByReferent). Like `thema` it narrows the
  // list that is already loaded — it is NOT one of the mutually exclusive server
  // filters below, so it survives an applyFilter/reset of those.
  referent: string;
  // Empty string until a range is picked, then the [von, bis] tuple.
  daterange: string | [Date | null, Date | null];
}

/** The minimal form-event shape the write actions call preventDefault on. */
interface FormEventLike {
  preventDefault?: () => void;
}

/**
 * One staged referent row — the create wizard's referenteArray entries and the
 * referenteAddData add-form bag. A loose form-data object.
 */
interface SeminarReferentInput {
  id_referent?: number | string;
  brutto_honorar_euro?: number | string;
  ist_mehrwert_steuer?: boolean;
  zahlung_methode?: string;
}

/**
 * The manual "add referent to the selected seminar" form (seminarReferentListInputs).
 * This is the ONE definition ReferentTab now reads directly off the store, so it
 * carries every field the tab touches: a picked referent's numeric id + display
 * name/vorname (spread in from the chosen Referent by currentReferentHandler and
 * read back by the autocomplete's value projection), and the honorar/MwSt fields
 * nullable to model a cleared input.
 */
export interface SeminarReferentListInput {
  seminar?: number | string;
  id_referent?: number;
  name?: string;
  vorname?: string;
  brutto_honorar_euro?: number | string | null;
  ist_mehrwert_steuer?: boolean | null;
  zahlung_methode?: string;
  sonstige_kosten_name?: string | null;
  sonstige_kosten_wert?: number | string | null;
}

/** The options bag for the create/copy/edit save (updateSeminar / saveSeminar). */
interface UpdateSeminarArgs {
  event?: FormEventLike | null;
  mode: OpenMode;
  data?: SeminarFormInput;
  wpToken: string;
}

/** The options bag for adding a referent to a seminar. */
interface AddReferentArgs {
  event?: FormEventLike | null;
  action?: string;
  idseminar?: number | string;
  semrefdata?: SeminarReferentInput;
  checkforlength?: boolean;
  id_wp_entry?: number | string;
  wpToken: string;
}

interface SeminarStoreState {
  // ---- State ---------------------------------------------------------------
  selectedSeminarId: number | null;
  activeSeminarQuery: SeminarQueryParams | null;

  // Filter flags (single source of truth, formerly in AppStateContext)
  idFilter: boolean;
  dateRangeFilter: boolean;
  // The raw filter form values (formerly useSeminarFilters `query`)
  filterQuery: FilterQuery;

  /**
   * The seminar grid's pagination page (0-based), remembered across the
   * select/edit round trip. Selecting a seminar collapses the grid to that single
   * row via the external filter, which drops AG Grid's own page index to 0 — so
   * without this the user was thrown back to page 1 after every edit and had to
   * click back to where they found the seminar. Reset whenever a filter changes
   * the result set (a new list makes the old page meaningless).
   */
  gridPage: number;

  // Edit state — editDraft is a DIFF overlaid on the live seminar, not a snapshot
  editSeminar: boolean;
  editDraft: Partial<Seminar> | null;
  formErrorHandling: StrapiFieldError[] | null;

  // Loading flags
  loading: boolean;
  loadingDyn: LoadingDyn;

  // Modal / section UI
  open: OpenMode;
  currentSection: number;

  // Referent add flow
  referenteArray: SeminarReferentInput[];
  referenteAddData: SeminarReferentInput;
  seminarReferentListInputs: SeminarReferentListInput;

  // Seminar detail panel UI (migrated from SeminarDetailsPanel + its detail hooks
  // so every tab/modal self-sources from the store instead of the panel drilling)
  /** Which seminar detail tab is open (formerly the panel's local `openTab`). */
  seminarOpenTab: number;
  /** The room-picker fuzzy-search query (formerly useSeminarRoomSelection local state). */
  roomQuery: RoomQuery;
  /** The referent-picker search query (formerly useSeminarReferentPicker local state). */
  referentQuery: string;
  /** The currently-selected participant rows (formerly useSeminarParticipants local state). */
  selectedParticipant: ParticipantRow[];
  /** Add-participant modal flag (formerly useSeminarParticipants local state). */
  addParticipant: boolean;
  /** Swap-participant modal flag (formerly useSeminarParticipants local state). */
  swapParticipant: boolean;
  // Delete-participant flag: the target row's documentId while a delete is pending,
  // `false` otherwise (set by the participants grid's delete cell). Named *Id to
  // avoid colliding with the `deleteParticipant` async action below.
  deleteParticipantId: string | false;

  // ---- Selection -----------------------------------------------------------
  selectSeminar: (id: number | null) => void;
  clearSelection: () => void;

  // ---- Filters -------------------------------------------------------------
  setFilter: (patch: Partial<FilterQuery>) => void;
  applyFilter: (args: { action: string; filter: boolean }) => Promise<void>;
  resetFilters: () => Promise<void>;

  /** Remember the seminar grid's current pagination page (0-based). */
  setGridPage: (page: number) => void;

  // ---- Data fetching -------------------------------------------------------
  getData: (params?: SeminarQueryParams | null) => Promise<Seminar[]>;
  refreshSeminars: () => Promise<void>;

  // ---- Editing -------------------------------------------------------------
  startEdit: () => void;
  cancelEdit: () => void;
  updateField: (name: string, value: FieldValue) => void;
  patchDraft: (patch: Partial<Seminar>) => void;

  // ---- Seminar create / update --------------------------------------------
  updateSeminar: (args: UpdateSeminarArgs) => Promise<void>;

  // ---- Referents / participants -------------------------------------------
  addReferent: (args: AddReferentArgs) => Promise<void>;
  /** Edit this seminar's honorar/MwSt for an already-attached referent. */
  updateSeminarReferent: (args: {
    vbSemRefId: string;
    ref_brutto_honorar_euro?: string | number | null;
    ist_mehrwert_steuer?: boolean | null;
  }) => Promise<void>;
  deleteReferent: (args: { vbSemRefId: string; wpToken: string }) => Promise<void>;
  deleteParticipant: (args: { documentId: string }) => Promise<void>;

  // ---- Referent-flow setters ----------------------------------------------
  setReferentListInputs: (updater: Updater<SeminarReferentListInput>) => void;
  setReferenteAddData: (u: Updater<SeminarReferentInput>) => void;
  setReferenteArray: (u: Updater<SeminarReferentInput[]>) => void;
  setCurrentSection: (u: Updater<number>) => void;
  setFormErrorHandling: (u: Updater<StrapiFieldError[] | null>) => void;

  // ---- Seminar detail panel UI setters ------------------------------------
  setSeminarOpenTab: (u: Updater<number>) => void;
  setRoomQuery: (u: Updater<RoomQuery>) => void;
  setReferentQuery: (u: Updater<string>) => void;
  setSelectedParticipant: (u: Updater<ParticipantRow[]>) => void;
  setAddParticipant: (u: Updater<boolean>) => void;
  setSwapParticipant: (u: Updater<boolean>) => void;
  setDeleteParticipantId: (u: Updater<string | false>) => void;

  // ---- Plain loading setters ----------------------------------------------
  setLoading: (value: boolean) => void;
  setLoadingDyn: (value: LoadingDyn) => void;

  // ---- Modal controls ------------------------------------------------------
  openCreate: () => void;
  openCopy: () => void;
  closeModal: () => void;
}

/** The empty filter shape (mirrors useSeminarFilters' initial `query`). */
const EMPTY_FILTER_QUERY: FilterQuery = {
  semid: "",
  thema: "",
  referent: "",
  daterange: "",
};

/** Read the on-screen list for the active filter straight from the query cache. */
const readActiveList = (activeSeminarQuery: SeminarQueryParams | null): Seminar[] =>
  (queryClient.getQueryData([...seminarsQueryKey, activeSeminarQuery ?? null]) as
    Seminar[] | undefined) ?? [];

/** Read the rooms list (['rooms'] query) for WordPress location resolution. */
const readRaumeSelection = (): Raeume[] =>
  (queryClient.getQueryData(roomsQueryKey) as Raeume[] | undefined) ?? [];

export const useSeminarStore = create<SeminarStoreState>()((set, get) => ({
  // ---- State ---------------------------------------------------------------
  selectedSeminarId: null,
  activeSeminarQuery: null,

  // Filter flags (single source of truth, formerly in AppStateContext)
  idFilter: false,
  dateRangeFilter: false,
  // The raw filter form values (formerly useSeminarFilters `query`)
  filterQuery: { ...EMPTY_FILTER_QUERY },
  gridPage: 0,

  // Edit state
  editSeminar: false,
  editDraft: null,
  formErrorHandling: null,

  // Loading flags
  loading: false,
  loadingDyn: false,

  // Modal / section UI
  open: false,
  currentSection: 1,

  // Referent add flow
  referenteArray: [],
  referenteAddData: {},
  seminarReferentListInputs: {},

  // Seminar detail panel UI. The object/array initials are created ONCE here (at
  // store init), so `roomQuery`/`selectedParticipant` keep a stable identity until
  // a setter replaces them — a per-render `{ bezeichnung: "" }` / `[]` would churn
  // identity through the picker hooks' memos/selectors.
  seminarOpenTab: 1,
  roomQuery: { bezeichnung: "" },
  referentQuery: "",
  selectedParticipant: [],
  addParticipant: false,
  swapParticipant: false,
  deleteParticipantId: false,

  // ---- Selection -----------------------------------------------------------
  selectSeminar: (id) => set({ selectedSeminarId: id }),
  clearSelection: () => set({ selectedSeminarId: null }),

  // ---- Filters -------------------------------------------------------------
  setFilter: (patch) => set((state) => ({ filterQuery: { ...state.filterQuery, ...patch } })),

  setGridPage: (page) =>
    set((state) => (state.gridPage === page ? state : { gridPage: Math.max(0, page) })),

  /**
   * Apply a specific filter: build the Strapi filter params for `action` from the
   * current form values (filterQuery), clear the current selection, point the
   * active query at those params (which fetches + warms the cache), then raise the
   * matching filter flag. The filters are mutually exclusive (a single active
   * query at a time), so applying one RESETS the others. (The old filterHandler
   * only reset on the ✕ path and let flags stack; resetting on apply is the
   * intended behaviour.)
   */
  applyFilter: async ({ action, filter }) => {
    const { filterQuery } = get();

    let params: SeminarQueryParams | undefined;
    if (action === "semid") {
      params = { "filters[id][$eq]": filterQuery.semid };
    } else if (action === "daterange") {
      // Open-ended range: Von only -> "from Von onward" (this replaced the old
      // single "from this date" search); Bis only -> "up to Bis"; both -> between.
      // Missing bounds are omitted so the range degrades to a single open bound.
      // A bound counts only if it's a real value: reject null/"" and (since the
      // range auto-applies on each keystroke) the Invalid Date a half-typed
      // field emits — but keep accepting ISO strings, which addDays parses.
      const [rawVon, rawBis] = Array.isArray(filterQuery.daterange) ? filterQuery.daterange : [];
      const validBound = (d: Date | string | null | undefined): Date | string | null => {
        if (!d) return null;
        if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
        return d;
      };
      const von = validBound(rawVon);
      const bis = validBound(rawBis);
      // datum/end_datum are native `date` columns — send bare "yyyy-MM-dd" bounds
      // (local components, TZ-safe), not Date objects (axios would emit a UTC ISO).
      const vonBare = von ? (toISODateString(addDays(von, 0)) as string) : null;
      const bisBare = bis ? (toISODateString(addDays(bis, 0)) as string) : null;
      params = {
        ...(vonBare && {
          "filters[$and][0][$or][0][datum][$gte]": vonBare,
          "filters[$and][0][$or][1][end_datum][$gte]": vonBare,
        }),
        ...(bisBare && {
          "filters[$and][1][$or][0][datum][$lte]": bisBare,
          "filters[$and][1][$or][1][end_datum][$lte]": bisBare,
        }),
      };
    }

    set({ loadingDyn: action });
    get().clearSelection();
    await get().getData(params);

    // Applying one filter resets the others: keep only the applied filter's form
    // value, clear the rest (incl. Thema, so a stale fuzzy match can't shadow the
    // new semID/date hit), and raise only its flag.
    set({
      filterQuery: {
        ...EMPTY_FILTER_QUERY,
        ...(action === "semid" && { semid: filterQuery.semid }),
        ...(action === "daterange" && { daterange: filterQuery.daterange }),
      },
      idFilter: action === "semid" ? filter : false,
      dateRangeFilter: action === "daterange" ? filter : false,
      loadingDyn: false,
      // A different result set makes the remembered page meaningless.
      gridPage: 0,
    });
  },

  /** Reset to the default (future-only) list and clear every filter flag. */
  resetFilters: async () => {
    get().clearSelection();
    await get().getData();
    set({
      filterQuery: { ...EMPTY_FILTER_QUERY },
      idFilter: false,
      dateRangeFilter: false,
      gridPage: 0,
    });
  },

  // ---- Data fetching -------------------------------------------------------
  /**
   * Point the active query at `params` and fetch it (warming the cache before the
   * useSeminarSelection observers subscribe). The single seminar-list fetch
   * bridge — the filter actions and useAppData's load-on-nav effect both call it.
   */
  getData: async (params = null) => {
    set({ activeSeminarQuery: params ?? null });
    try {
      return await queryClient.fetchQuery(seminarsQueryOptions(params));
    } catch (err) {
      // Surface the failure — the useSeminarSelection observers only read `.data`,
      // so without this a failed filter/search just shows an EMPTY grid,
      // indistinguishable from "no results". Return [] so the fire-and-forget
      // callers (applyFilter, load-on-nav) don't see an unhandled rejection.
      notify("error", getErrorMessage(err));
      return [];
    }
  },

  /** Post-mutation refresh: invalidate (and refetch) the seminar list. */
  refreshSeminars: () => queryClient.invalidateQueries({ queryKey: seminarsQueryKey }),

  // ---- Editing -------------------------------------------------------------
  /**
   * Enter edit mode. The draft is a DIFF overlaid on the live seminar (see
   * useSeminarInputs / updateSeminar), not a full snapshot — so it starts as
   * identity only. This keeps server-derived collections (referent[],
   * participations) live: a mid-edit refetch (add/remove referent, delete
   * participant) surfaces immediately instead of being masked by a stale copy.
   */
  startEdit: () => {
    const { activeSeminarQuery, selectedSeminarId } = get();
    const found = readActiveList(activeSeminarQuery).find((s) => s.id === selectedSeminarId);
    set({
      editSeminar: true,
      editDraft: found ? { id: found.id, documentId: found.documentId } : null,
      formErrorHandling: null,
    });
  },

  cancelEdit: () => set({ editSeminar: false, editDraft: null, formErrorHandling: null }),

  /**
   * Update one draft field. Sanitizes the value (date-only -> YYYY-MM-DD; the
   * time-of-day fields keep their Date) and clears any form error for that field.
   */
  updateField: (name, value) =>
    set((state) => ({
      editDraft: {
        ...(state.editDraft ?? {}),
        [name]: sanitizeSeminarValue(name, value),
      },
      formErrorHandling: state.formErrorHandling
        ? state.formErrorHandling.filter((e) => e.path?.[0] !== name)
        : state.formErrorHandling,
    })),

  patchDraft: (patch) => set((state) => ({ editDraft: { ...(state.editDraft ?? {}), ...patch } })),

  // ---- Seminar create / update (ported from updateSeminarHandler) ----------
  updateSeminar: async ({ event, mode, data, wpToken }) => {
    // Edit sends the live seminar with the draft diff layered on top, so the
    // payload carries every field (untouched ones from the cache) even though the
    // draft only holds what the user changed. Add/copy uses the passed data.
    let source: SeminarFormInput;
    if (mode === "edit") {
      const { activeSeminarQuery, selectedSeminarId, editDraft } = get();
      const live = readActiveList(activeSeminarQuery).find((s) => s.id === selectedSeminarId);
      source = { ...(live ?? {}), ...(editDraft ?? {}) };
    } else {
      source = data ?? {};
    }
    const raumSource = { ...(source ?? {}) };
    set({ loading: true });

    try {
      let result: ApiResponse<Seminar> | undefined;

      if (mode === "edit") {
        // Typed serializer: the exact allowed field set, relations flattened to id.
        const payload = toSeminarPayload(source);
        // Partial form state -> the entity's identity at the write choke point
        // (apiId reads only id/documentId, both present on the live seminar).
        result = await seminarApi.update(apiId(source as Identifiable), payload);
      }

      if (mode === "add" || mode === "copy") {
        // toSeminarCreatePayload layers the new-seminar overrides on the base
        // payload: strips vb_sem_refs, nulls the WP linkage (so a copy creates a
        // NEW WP post), computes zahl_termin/frage_termin from end_datum, and
        // defaults max_teilnehmer to 0. See services/serializers/seminar.ts.
        const createData = toSeminarCreatePayload(data ?? {});
        result = await seminarApi.create(createData);
      }

      if (result?.data) {
        const created = unwrap(result);

        // The seminar is persisted — confirm to the user NOW, BEFORE the
        // best-effort WordPress sync/refresh below. A WP or refresh failure must
        // never hide this success: if it did, the user would see only an error,
        // the wizard would stay open, and re-submitting would create a DUPLICATE.
        notify("info", MSG.seminar.saved(mode === "edit"));
        if (mode === "add" || mode === "copy") {
          notify("info", MSG.seminar.newId(created.id), {
            position: "top-center",
            autoClose: false,
          });
          if (navigator?.clipboard && created?.id) {
            // writeText wants a string; swallow a denied-permission reject.
            navigator.clipboard.writeText(String(created.id)).catch(() => {});
          }
        }

        // Best-effort WordPress sync + linkage — a failure is logged, not fatal
        // (the seminar already exists). The create/update response doesn't populate
        // `raum`, so merge the submitted room id for acf[city]/[location]/state.
        let wpResult: WpPostResult | undefined;
        try {
          wpResult = await syncSeminarToWp({
            data: {
              ...created,
              raum: relationId(raumSource.raum),
            },
            raumeSelection: readRaumeSelection(),
            wpToken,
          });
          if (wpResult?.id && (mode === "add" || mode === "copy")) {
            if (created.documentId) {
              await seminarApi.update(created.documentId, toSeminarWpLinkagePayload(wpResult));
            } else {
              console.error("seminarStore.updateSeminar: documentId missing from create response");
            }
          }
        } catch (wpErr) {
          console.warn(
            "seminarStore.updateSeminar: WordPress sync failed (seminar was saved)",
            wpErr
          );
        }

        const { referenteArray } = get();
        if (referenteArray.length > 0) {
          (async () => {
            for await (const [index, referent] of referenteArray.entries()) {
              await get().addReferent({
                event,
                action: "new",
                idseminar: created.id,
                semrefdata: referent,
                checkforlength: index === referenteArray.length - 1,
                id_wp_entry: wpResult?.id,
                wpToken,
              });
            }
          })();
        }

        set({ editSeminar: false });
        try {
          await get().refreshSeminars();
        } catch (refreshErr) {
          console.warn("seminarStore.updateSeminar: refresh after save failed", refreshErr);
        }
        set({ loading: false, open: false, currentSection: 1 });
        window.scrollTo({ top: 0, behavior: "smooth" });
        set({ referenteAddData: { ist_mehrwert_steuer: true }, referenteArray: [] });
        // Clear the lingering source selection after add/copy. Edit keeps its
        // selection so the grid re-selects it once the list refetches.
        if (mode !== "edit") {
          get().clearSelection();
        }
      } else {
        // A 2xx with no body is anomalous — surface it instead of hanging the
        // "loading" spinner with no feedback.
        set({ loading: false });
        notify("error", MSG.common.error);
      }
    } catch (err) {
      // getErrorMessage covers both the Strapi (data.error.message) and the flat
      // WordPress (data.message) shapes this catch used to hand-drill.
      set({ formErrorHandling: getFieldErrors(err), loading: false });
      notify("error", getErrorMessage(err));
    }
  },

  // ---- Referents (ported from addSeminarReferentHandler) -------------------
  /**
   * Add a referent to a seminar. `action: 'add'` reads the values from
   * seminarReferentListInputs (manual add on the selected seminar); `action:
   * 'new'` uses the passed `semrefdata` (the create-time referenteArray loop).
   */
  addReferent: async ({
    event,
    action = "add",
    idseminar,
    semrefdata,
    checkforlength,
    id_wp_entry,
    wpToken,
  }) => {
    const { seminarReferentListInputs, activeSeminarQuery, selectedSeminarId } = get();
    const { seminar, id_referent, brutto_honorar_euro, ist_mehrwert_steuer, zahlung_methode } =
      seminarReferentListInputs;

    event?.preventDefault?.();

    // The referent fields come from `semrefdata` when adding a brand-new referent
    // inline ("new"), otherwise from the picked existing referent (the list
    // inputs). Branch the SOURCE once instead of repeating `action === "new"` per
    // field.
    const src =
      action === "new"
        ? {
            id_seminar: idseminar,
            id_referent: semrefdata?.id_referent,
            ref_brutto_honorar_euro: semrefdata?.brutto_honorar_euro,
            ist_mehrwert_steuer: semrefdata?.ist_mehrwert_steuer,
            zahlung_methode: semrefdata?.zahlung_methode,
          }
        : {
            id_seminar: seminar,
            id_referent,
            ref_brutto_honorar_euro: brutto_honorar_euro,
            ist_mehrwert_steuer,
            zahlung_methode,
          };

    try {
      const result = await referentApi.createSeminarReferent({
        ...src,
        sonstige_kosten_name: seminarReferentListInputs.sonstige_kosten_name || null,
        sonstige_kosten_wert: seminarReferentListInputs.sonstige_kosten_wert || null,
      });

      if (result?.data) {
        if (action === "new" && !checkforlength) {
          return;
        }

        // The selected seminar in cache backs the non-'new' add's id_wp_entry.
        const selectedSeminar = readActiveList(activeSeminarQuery).find(
          (s) => s.id === selectedSeminarId
        );

        // Try WP update but don't let it block the main flow
        try {
          await syncReferentsToWp({
            seminarId: idseminar,
            id_wp_entry:
              action === "new"
                ? id_wp_entry
                : selectedSeminar?.id_wp_entry
                  ? selectedSeminar.id_wp_entry
                  : null,
            raumeSelection: readRaumeSelection(),
            wpToken,
          });
        } catch (wpErr) {
          console.warn("seminarStore.addReferent: WordPress sync failed", wpErr);
        }

        try {
          await get().refreshSeminars();
        } catch (refreshErr) {
          console.warn("seminarStore.addReferent: seminar refresh failed", refreshErr);
        }

        notify("info", MSG.seminar.referentAdded);
        get().setReferentListInputs({});
      } else {
        // 2xx with no body — refresh so the referent list reflects the real state
        // instead of silently doing nothing.
        await get().refreshSeminars();
      }
    } catch (err) {
      console.error("seminarStore.addReferent failed", err);
      notify("error", getErrorMessage(err));
    }
  },

  /**
   * Change what a referent is paid FOR THIS SEMINAR.
   *
   * The honorar and the MwSt flag are columns on the vb-sem-ref junction, seeded
   * from the referent's profile when the referent is attached — so writing them here
   * is per-seminar and leaves the referent's own default alone. The backend
   * controller normalizes the amount ("1.234,50" -> "1234.50") on update exactly as
   * it does on create.
   */
  updateSeminarReferent: async ({ vbSemRefId, ref_brutto_honorar_euro, ist_mehrwert_steuer }) => {
    set({ loadingDyn: "update_referent" });
    try {
      await referentApi.updateSeminarReferent(vbSemRefId, {
        // Only send what the caller actually changed: an absent key must not blank
        // the other column.
        ...(ref_brutto_honorar_euro !== undefined && { ref_brutto_honorar_euro }),
        ...(ist_mehrwert_steuer !== undefined && { ist_mehrwert_steuer }),
      });
      notify("info", MSG.seminar.referentUpdated);
      await get().refreshSeminars();
    } catch (err) {
      console.error("seminarStore.updateSeminarReferent failed", err);
      notify("error", getErrorMessage(err));
    } finally {
      set({ loadingDyn: false });
    }
  },

  /** Delete a seminar↔referent junction row, then re-sync WP + refresh. */
  deleteReferent: async ({ vbSemRefId, wpToken }) => {
    const { activeSeminarQuery, selectedSeminarId } = get();

    try {
      // Strict: Assume vbSemRefId is already a documentId
      const result = await referentApi.deleteSeminarReferent(vbSemRefId);

      // delete may return an empty/different structure — just check it resolved
      if (result) {
        const selectedSeminar = readActiveList(activeSeminarQuery).find(
          (s) => s.id === selectedSeminarId
        );

        try {
          await syncReferentsToWp({
            seminarId: selectedSeminar?.id,
            id_wp_entry: selectedSeminar?.id_wp_entry ? selectedSeminar.id_wp_entry : null,
            raumeSelection: readRaumeSelection(),
            wpToken,
          });
        } catch (wpErr) {
          console.warn("seminarStore.deleteReferent: WordPress sync failed", wpErr);
        }

        try {
          await get().refreshSeminars();
        } catch (refreshErr) {
          console.warn("seminarStore.deleteReferent: seminar refresh failed", refreshErr);
        }

        notify("info", MSG.seminar.referentDeleted);
        get().setReferentListInputs({});
      }
    } catch (err) {
      console.error("seminarStore.deleteReferent failed", err);
      notify("error", getErrorMessage(err));
    }
  },

  // ---- Participants (ported from participantDeleteHandler) -----------------
  deleteParticipant: async ({ documentId }) => {
    let result: Awaited<ReturnType<typeof seminarParticipantApi.delete>> | undefined;
    try {
      // Strict: Assume documentId is a documentId
      result = await seminarParticipantApi.delete(documentId);
    } catch (err) {
      console.error("seminarStore.deleteParticipant failed", err);
      notify("error", getErrorMessage(err));
    }

    // Refetch regardless — the participants grid re-derives from the refreshed
    // list, so a deleted row disappears (real feedback) even when the response
    // body is empty. That includes a failed delete: a 404 means the row is
    // already gone (another tab or user removed it), and without the refetch it
    // stayed on screen inviting another doomed click. Only claim success when the
    // delete actually confirmed. The client lists too: the last participant of a
    // company leaving takes the company out of the seminar's client list
    // (['clients', { seminar_id }]).
    await Promise.all([
      get().refreshSeminars(),
      queryClient.invalidateQueries({ queryKey: clientsQueryKey }),
    ]);
    if (result?.data) {
      notify("info", MSG.seminar.participantDeleted);
    }
  },

  // ---- Referent-flow setters (accept functional updaters) ------------------
  setReferentListInputs: (updater) =>
    set((state) => ({
      seminarReferentListInputs: applyUpdater(updater, state.seminarReferentListInputs),
    })),
  ...makeSetters(set, [
    "referenteAddData",
    "referenteArray",
    "currentSection",
    "formErrorHandling",
    "seminarOpenTab",
    "roomQuery",
    "referentQuery",
    "selectedParticipant",
    "addParticipant",
    "swapParticipant",
    "deleteParticipantId",
  ]),

  // ---- Plain loading setters ----------------------------------------------
  setLoading: (value) => set({ loading: value }),
  setLoadingDyn: (value) => set({ loadingDyn: value }),

  // ---- Modal controls ------------------------------------------------------
  openCreate: () => set({ open: "add" }),
  openCopy: () => set({ open: "copy" }),
  /** Cancel the create/copy wizard: close it and reset all its staging state. */
  closeModal: () =>
    set({
      open: false,
      currentSection: 1,
      formErrorHandling: null,
      referenteAddData: { ist_mehrwert_steuer: true },
      referenteArray: [],
      seminarReferentListInputs: {},
    }),
}));
